/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { InsightsOverview } from './insights/overview';
import SummaryContainer from '../../components/summary';
import { RootState, useAppDispatch, useAppSelector } from '../../reducers';
import { getRootOrg, isInitiativeCTL } from '../../selectors/initiative';
import { Summary } from './summary';
import { InsightPage } from './insights/utils/constants';
import { Button, ButtonGroup } from 'reactstrap';
import { getInsightPage, isInsightLayoutPage } from './insights/utils/helpers';
import { DashboardRow, DashboardSectionTitle } from '../../components/dashboard';
import { Layout } from './insights/partials/Layout';
import { getLayout, LayoutColumn, layoutOptions, LayoutRow } from './insights/utils/layout';
import { getMainDownloadCode, MainDownloadCode } from '../../config/app-config';
import G17Client from '../../services/G17Client';
import { canAccessInsightsAndDownloads, canManageInitiativeId } from '../../selectors/user';
import { PeriodDropdown } from './insights/partials/PeriodDropdown';
import { DataPeriods } from '../../types/universalTracker';
import './insights/styles.scss';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import IconButton from '../../components/button/IconButton';
import { CompanyProfile } from '../../components/company-profile';
import NotAuthorised from '../not-authorised';
import { BaseInsightProps } from './insightTypes';
import { useSiteAlert } from '../../hooks/useSiteAlert';

/** @deprecated */
export const Insights = (props: BaseInsightProps) => {
  const { initiativeId, surveyId } = props;

  const dispatch = useAppDispatch();
  const match = useRouteMatch<{ summaryPage?: InsightPage }>();
  const { addSiteError } = useSiteAlert();

  const isCompanyTrackerLight = useAppSelector(isInitiativeCTL);
  const rootInitiative = useAppSelector(getRootOrg);
  const appConfigCode = rootInitiative?.appConfigCode;

  const initiative = useAppSelector((state: RootState) => state.initiative.data);
  const canAccessInsightsByRole = useAppSelector((state) => canAccessInsightsAndDownloads(state, initiativeId));
  const canManage = useAppSelector((state) => canManageInitiativeId(state, initiativeId));

  const summaryPage = getInsightPage(match?.params?.summaryPage);
  const showReportSwitcher = !summaryPage || summaryPage === InsightPage.Overview;

  const mainDownloadCode = getMainDownloadCode(appConfigCode, rootInitiative?.permissionGroup);
  const [selectedPack, setSelectedPack] = useState<MainDownloadCode>(mainDownloadCode);

  const [highlights, setHighlights] = useState<LayoutColumn[]>([]);

  // set the period in the url instead of the state to keep it on page refresh
  const location = useLocation();
  const period = new URLSearchParams(location.search).get('period') as DataPeriods;

  React.useEffect(() => {
    if (!initiativeId) {
      return
    }
    G17Client.getLayoutHighlightSettings(initiativeId)
      .then((data) => {
        setHighlights(data ?? []);
      })
      .catch((e) => {
        addSiteError(e);
      });

  }, [dispatch, addSiteError, initiativeId, selectedPack]);

  const addHighlight = async (column: LayoutColumn) => {
    const added = [...highlights, column];
    return G17Client.setLayoutHighlightSettings(initiativeId, added)
      .then(() => {
        dispatch(
          addSiteAlert({
            content: 'Chart added to Overview page',
            timeout: 2000,
            color: SiteAlertColors.Success,
          })
        );
        return setHighlights(added);
      })
      .catch((e) => addSiteError(e));
  };

  const removeHighlight = async (column: LayoutColumn) => {
    const filtered = highlights.filter((el) => el.title !== column.title);
    return G17Client.setLayoutHighlightSettings(initiativeId, filtered)
      .then(() => setHighlights(filtered))
      .catch((e) => addSiteError(e));
  };

  // for the highligt page only, merge the highlights to the current layout rows
  const mergeHighlightsLayout = (overviewLayout: LayoutRow[]) => {
    const newLayout = [...overviewLayout]
    //rows of 3 charts
    for (let i = 0; i < highlights.length; i += 3) {
      const chunk = highlights.slice(i, i + 3);
      newLayout.push({ columns: chunk, columnCount: 3 });
    }
    return newLayout
  }

  const readOnly = isCompanyTrackerLight ? true : props.readOnly;

  const layoutButtons = mainDownloadCode === 'sgx_metrics' && isInsightLayoutPage(summaryPage) ?
    <ButtonGroup>
      {layoutOptions.map(option => {
        const isActive = selectedPack === option.code;
        return <Button
          key={option.code}
          outline={!isActive}
          active={isActive}
          color={'primary'}
          onClick={() => setSelectedPack(option.code)}>
          {option.name}
        </Button>
      })}</ButtonGroup> : undefined;

  const fixedlayoutColIds = getLayout(InsightPage.Overview, selectedPack).reduce((acc, row) => {
    const rowTitles = row.columns.map(col => col.refCode)
    acc.push(...rowTitles)
    return acc
  }, [] as (string | undefined)[])

  const isColumnInFixedHighlights = (col: LayoutColumn) => {
    return fixedlayoutColIds.includes(col.refCode)
  }
  const highlightColIds = highlights.map(col => col.refCode)
  const isColumnInHighlights = (col: LayoutColumn) => {
    return highlightColIds.includes(col.refCode)
  }

  const getHighlightButton = (col: LayoutColumn) => {
    if (!canManage) {
      return;
    }
    if (!isColumnInHighlights(col) && !isColumnInFixedHighlights(col)) {
      return <div className='float-end ml-auto mt-auto addbtn'>
        <span className='text-sm'>Add to overview</span>
        <IconButton icon='fa-plus' onClick={() => addHighlight(col)} />
      </div>
    }
    if (isColumnInHighlights(col) && !isInsightLayoutPage(summaryPage)) {
      return <div className='float-end ml-auto mt-auto addbtn'>
        <span className='text-sm'>Remove</span>
        <IconButton icon='fa-close' className='remove' onClick={() => removeHighlight(col)} />
      </div>
    }
  }

  if (!canAccessInsightsByRole) {
    return <NotAuthorised />;
  }

  return (
    <SummaryContainer
      surveyId={surveyId}
      className='insights-dashboard'
      showReportSwitcher={showReportSwitcher}
      initiativeId={initiativeId}
      buttons={layoutButtons ? [layoutButtons] : undefined}
    >
      {(() => {
        // esg pages
        if (isInsightLayoutPage(summaryPage)) {
          return (
            <>
              <DashboardRow
                title='Highlights'
                headingStyle={2}
                className='mt-4 text-ThemeHeadingLight'
                mb={2}
                buttons={[<PeriodDropdown initiativeId={initiativeId} selectedPack={selectedPack} period={period} />]}
              />
              <Layout getHighlightButton={getHighlightButton} layout={getLayout(summaryPage, selectedPack)} />
            </>
          );
        }
        if (isCompanyTrackerLight) {
          return <>
            <CompanyProfile initiative={initiative} readOnly={props.readOnly} />
            <DashboardSectionTitle title='Highlights' headingStyle={2} className='text-ThemeHeadingLight' />
            <Layout getHighlightButton={getHighlightButton} layout={mergeHighlightsLayout(getLayout(InsightPage.Overview, selectedPack))} />
            <InsightsOverview {...props} selectedPack={selectedPack} readOnly={readOnly} />
          </>
        }
        return <Summary {...props} selectedPack={selectedPack} readOnly={readOnly} getHighlightButton={getHighlightButton} layout={mergeHighlightsLayout(getLayout(InsightPage.Overview, selectedPack))} />
      })()
      }
    </SummaryContainer>
  );
}

